// apps/web/src/lib/api-client.ts
//
// Thin typed fetch wrapper for non-Better-Auth API calls (e.g. /api/invitations,
// /api/recipes). Better-Auth's own React client owns /api/auth/* calls.
//
// Contract:
//   - Always sends cookies (credentials: include) so session cookies travel.
//   - Treats any non-2xx as an error; throws ApiError with the response body.
//   - Prepends VITE_AUTH_BASE_URL (the API origin) to relative paths.

const baseURL = import.meta.env["VITE_AUTH_BASE_URL"] ?? "http://localhost:3000";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const url = path.startsWith("http") ? path : `${baseURL}${path}`;
  const headers = new Headers(opts?.headers);
  if (!headers.has("Content-Type") && opts?.body) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(url, {
    ...opts,
    credentials: "include",
    headers,
  });
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await response.json() : await response.text();
  if (!response.ok) {
    const message =
      typeof payload === "object" && payload !== null && "message" in payload
        ? String((payload as { message: unknown }).message)
        : `Request failed: ${response.status}`;
    throw new ApiError(response.status, payload, message);
  }
  return payload as T;
}
